import {
  Card,
  CardBody,
  Image,
  Link,
  Pagination,
  Select,
  SelectItem,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import Placeholder from '@/assets/images/file-search.png';
import useSegment from '@/hooks/useSegment';
import { copyAndformatVisualQuestions } from '@/patient-app-common/helpers/progress_helper';
import useSurveys from '@/patient-app-common/hooks/useSurveys';
import useTrackers from '@/patient-app-common/hooks/useTrackers';

const SELECTOR_EVENT = 'Past survey dropdown list selected';

export function TrackerList() {
  const navigate = useNavigate();
  const { allSurveys: surveys, isLoading: isLoadingSurveys } = useSurveys();
  const { surveyEntries, isLoading: isLoadingEntries } = useTrackers();
  const isLoading = isLoadingEntries || isLoadingSurveys;

  const { t } = useTranslation('progress');
  const { sendTrackEvent } = useSegment();

  const [selectedId, setSelectedId] = useState(0);

  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const selectedSurveyEntries = useMemo(() => {
    if (!surveyEntries?.length || !surveys?.length) return [];
    const surveyIds = _.map(surveys, 'id');

    let entries = [...surveyEntries]
      .filter((entry) => surveyIds.includes(entry.procedureId))
      .sort((a, b) => dayjs(b.createdAt) - dayjs(a.createdAt));
    if (selectedId) {
      entries = _.filter(entries, ['procedureId', selectedId]);
    }

    return entries;
  }, [selectedId, surveyEntries]);

  const chartQuestions = useMemo(() => {
    if (!surveyEntries?.length || !surveys) return [];
    return copyAndformatVisualQuestions(surveys, surveyEntries);
  }, [surveys, surveyEntries]);

  const totalPages = useMemo(() => {
    if (_.isEmpty(selectedSurveyEntries)) return 0;
    return Math.ceil(selectedSurveyEntries.length / itemsPerPage);
  }, [selectedSurveyEntries]);

  const handleSelectChange = (e) => {
    setCurrentPage(1);

    const id = _.toNumber(e.target.value);
    setSelectedId(id);

    const procedure = _.find(surveys, ['id', id]);
    if (!procedure) return;

    sendTrackEvent(SELECTOR_EVENT, {
      procedureId: procedure.id,
      procedureName: procedure.patientDisplayName,
    });
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEntries = selectedSurveyEntries.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  if (!isLoading && (!surveys || !surveyEntries?.length)) {
    return (
      <div className="flex justify-center">
        <div className="text-center w-full">
          <div className="flex justify-center py-8">
            <Image src={Placeholder} alt="" width={400} />
          </div>
          <h2 className="font-light text-default-600 text-lg">
            {t('chartslist.empty_text')}
          </h2>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-8">
        <Select
          className="max-w-xs"
          label={t('common.text.survey')}
          placeholder={t('common.select.placeholder')}
          defaultSelectedKeys={[selectedId]}
          variant="bordered"
          onChange={(e) => handleSelectChange(e)}
        >
          {surveys.map((survey) => (
            <SelectItem key={survey.id} value={survey.id}>
              {survey.mainTracker
                ? t('common.text.main_tracker')
                : survey.patientDisplayName}
            </SelectItem>
          ))}
        </Select>
        {!_.isEmpty(chartQuestions) && (
          <Card
            isPressable
            onPress={() => navigate(`/progress/surveys/charts`)}
            shadow="sm"
          >
            <CardBody>
              <div className="flex justify-between">
                <h2>{t('common.link.charts')}</h2>
                <Icon
                  icon="mdi:chevron-right"
                  className="text-xl place-self-center"
                />
              </div>
            </CardBody>
          </Card>
        )}
        <div className="space-y-4">
          {paginatedEntries.map((entry) => {
            const survey = _.find(surveys, ['id', entry.procedureId]);
            if (!survey) return null;

            const { result } = entry;
            const surveyName = survey.mainTracker
              ? t('common.text.main_tracker')
              : survey.patientDisplayName;

            return (
              <Card key={entry.id} shadow="sm">
                <CardBody>
                  <div className="inline-flex">
                    <div className="pr-16">
                      <IconCalendar date={entry.createdAt} />
                    </div>
                    <div>
                      <h2 className="font-semibold text-lg">{surveyName}</h2>
                      {result && (
                        <div
                          className="rounded inline-flex p-2 my-2"
                          style={{
                            backgroundColor:
                              result.apptext.recRecommendationBackgroundColor,
                          }}
                        >
                          <img
                            className="inline-flex w-6 h-6"
                            src={`/icons/${result.apptext.recIconImage}`}
                            alt=""
                          />
                          <p className="text-background">
                            {result.apptext.recLabelText}
                          </p>
                        </div>
                      )}
                      <div>
                        <Link href={`/progress/surveys/${entry.id}`} size="sm">
                          {t('trackerlist.link.survey')}
                          <Icon
                            icon="mdi:chevron-right"
                            className="inline-flex"
                          />
                        </Link>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      </div>
      {selectedSurveyEntries.length > itemsPerPage && (
        <Pagination
          color="primary"
          page={currentPage}
          showControls
          total={totalPages}
          onChange={setCurrentPage}
        />
      )}
    </>
  );
}

function IconCalendar({ date }) {
  return (
    <div className="min-w-24 bg-background min-h-32 font-medium">
      <div className="border rounded-lg w-24 flex-none text-center">
        <div className="block rounded-t overflow-hidden text-center">
          <div className="bg-primary-500 text-background py-1">
            {dayjs(date).format('MMM')}
          </div>
          <div>
            <span className="text-3xl font-bold leading-tight">
              {dayjs(date).format('D')}
            </span>
          </div>
          <div>
            <span className="text-sm">{dayjs(date).format('dddd')}</span>
          </div>
          <div>
            <span className="font-light text-xs">
              {dayjs(date).format('YYYY')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
